import React, { useEffect, useMemo, useState } from 'react';

import { Box, FormControl, MenuItem, OutlinedInput, Paper, Select, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { managementReportingYearOptions } from '../../../shared/utils/reportingYear';
import { SCROLLABLE_SELECT_MENU_PROPS } from '../../../shared/components/selectMenuProps';
import ActionPlanDialog from '../../climateAction/components/ActionPlanDialog';
import ActionPlanDetailDialog from '../../climateAction/components/ActionPlanDetailDialog';
import ActionPlanList from '../../climateAction/components/ActionPlanList';
import ActionPlanResultDialog from '../../climateAction/components/ActionPlanResultDialog';
import TargetConfigurationList from '../../climateAction/components/TargetConfigurationList';
import TargetDialog from '../../climateAction/components/TargetDialog';
import TargetPeriodConfirmDialog from '../../climateAction/components/TargetPeriodConfirmDialog';
import useClimateActionPlanMaster from '../../climateAction/hooks/useClimateActionPlanMaster';
import useClimateTargetMaster from '../../climateAction/hooks/useClimateTargetMaster';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import AppToast from '../../../shared/components/AppToast';

const MANAGEMENT_TABS = {
  ACTION_PLAN: 'action-plan',
  TARGETS: 'targets',
};

/** Master Data screen hosting the Action Plan and Target management tabs. */
export default function ClimateManagementPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(MANAGEMENT_TABS.ACTION_PLAN);
  const [planFiscalYear, setPlanFiscalYear] = useState(() => new Date().getUTCFullYear());
  const [planFilter, setPlanFilter] = useState('all');
const [planDialogOpen, setPlanDialogOpen] = useState(false);
const [editingPlan, setEditingPlan] = useState(null);
const [detailPlan, setDetailPlan] = useState(null);
const [resultPlan, setResultPlan] = useState(null);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [selectedTargetCycleId, setSelectedTargetCycleId] = useState(null);
  const [targetDialogMode, setTargetDialogMode] = useState('edit');
  const [confirmTargetAction, setConfirmTargetAction] = useState(null);
  const [toast, setToast] = useState(null);

  const planMaster = useClimateActionPlanMaster();
  const targetMaster = useClimateTargetMaster();

  const yearOptions = useMemo(
    () => managementReportingYearOptions({ yearsAhead: 1 }),
    [],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const fy = params.get('fy');
    const filter = params.get('filter');
    if (tab === MANAGEMENT_TABS.TARGETS) setActiveTab(tab);
    else setActiveTab(MANAGEMENT_TABS.ACTION_PLAN);
    if (fy && yearOptions.includes(Number(fy))) setPlanFiscalYear(Number(fy));
    if (filter) setPlanFilter(filter);
  }, [location.search, yearOptions]);

  const openPlanDialog = (plan = null) => {
    setEditingPlan(plan);
    setPlanDialogOpen(true);
  };

  const savePlan = async (draft, planId = null) => {
    const result = await planMaster.saveActionPlan(draft, planId);
    setToast({ severity: 'success', message: t(planId ? 'common.feedback.update.success' : 'common.feedback.create.success') });
    return result;
  };

  const savePlanResult = async (planId, fiscalYear, result, status) => {
    const savedResult = await planMaster.saveActionPlanResult(planId, fiscalYear, result, status);
    setToast({ severity: 'success', message: t('common.feedback.update.success') });
    return savedResult;
  };

  const removePlan = async (planId) => {
    try {
      await planMaster.deleteActionPlan(planId);
      setToast({ severity: 'success', message: t('common.feedback.delete.success') });
    } catch (error) {
      setToast({ severity: 'error', message: resolveUserFacingError(error, t('common.feedback.save.failed')) });
    }
  };

  const bulkTransitionPlans = async (planIds, targetStatus) => {
    try {
      const result = await planMaster.bulkTransitionActionPlans(planIds, targetStatus);
      const failedCount = result?.failed?.length || 0;
      if (failedCount > 0) {
        setToast({ severity: 'error', message: t('climateAction.bulk.partialFailed', { count: failedCount }) });
      } else {
        setToast({ severity: 'success', message: t('climateAction.bulk.updated', { count: planIds.length }) });
      }
    } catch (error) {
      setToast({ severity: 'error', message: resolveUserFacingError(error, t('common.feedback.save.failed')) });
    }
  };

  const openTargetDialog = (cycle = null, mode = 'edit') => {
    setSelectedTargetCycleId(cycle?.id || null);
    setTargetDialogMode(mode);
    setTargetDialogOpen(true);
  };

  const confirmTargetPeriodAction = async (cycle, action) => {
    try {
      if (action === 'archive') {
        await targetMaster.archiveTargetPeriod(cycle.id);
      } else {
        await targetMaster.deleteTargetPeriod(cycle.id);
      }
      setToast({ severity: 'success', message: t('climateAction.feedback.targetSaved') });
    } catch (error) {
      setToast({ severity: 'error', message: resolveUserFacingError(error, t('common.feedback.save.failed')) });
    } finally {
      setConfirmTargetAction(null);
    }
  };

  const fiscalYearLabel = t('climateAction.yearLabel');

  const yearSelector = (value, onChange) => (
    <FormControl size="small" sx={{ minWidth: { sm: 116 }, width: { xs: '100%', sm: 'auto' } }}>
      <Select
        MenuProps={SCROLLABLE_SELECT_MENU_PROPS}
        value={value}
        onChange={event => onChange(Number(event.target.value))}
        input={<OutlinedInput size="small" />}
        inputProps={{ 'aria-label': fiscalYearLabel }}
        sx={{ width: '100%' }}
      >
        {yearOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <Box component="section" aria-labelledby="climate-management-title" className="page-stack climate-management-shell" sx={{ minWidth: 0 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography id="climate-management-title" variant="h6" fontWeight={700}>
              {t('climateAction.management.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {t('climateAction.management.subtitle')}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ mt: 1.5, mb: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(event, nextTab) => setActiveTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 48, px: 1.5, '& .MuiTab-root': { minHeight: 48, minWidth: { xs: 104, sm: 116 }, px: { xs: 1.5, sm: 2 }, textTransform: 'none' } }}
        >
          <Tab value={MANAGEMENT_TABS.ACTION_PLAN} label={t('climateAction.tabs.actions')} />
          <Tab value={MANAGEMENT_TABS.TARGETS} label={t('climateAction.tabs.targets')} />
        </Tabs>
      </Paper>

      {activeTab === MANAGEMENT_TABS.ACTION_PLAN && (
        <ActionPlanList
          actionPlans={planMaster.actionPlans}
          canMutate
          error={planMaster.error}
          loading={planMaster.loading}
          initialFilter={planFilter}
          onAdd={() => openPlanDialog(null)}
          onBulkTransition={bulkTransitionPlans}
          onDelete={removePlan}
          onEdit={openPlanDialog}
          onOpenResult={plan => setResultPlan(plan)}
          onSearch={planMaster.load}
          onView={plan => setDetailPlan(plan)}
          selectedYear={planFiscalYear}
          t={t}
          yearSelector={yearSelector(planFiscalYear, setPlanFiscalYear)}
        />
      )}

      {activeTab === MANAGEMENT_TABS.TARGETS && (
        <TargetConfigurationList
          baselineYears={targetMaster.baselineYears}
          canMutate
          onAdd={() => openTargetDialog(null, 'create')}
          onArchive={cycle => setConfirmTargetAction({ action: 'archive', cycle })}
          onDelete={cycle => setConfirmTargetAction({ action: 'delete', cycle })}
          onEdit={cycle => openTargetDialog(cycle, 'edit')}
          onView={cycle => openTargetDialog(cycle, 'view')}
          targetCycles={targetMaster.targetCycles}
          targets={targetMaster.targets}
          t={t}
        />
      )}

      <ActionPlanDialog
        canMutate
        onClose={() => setPlanDialogOpen(false)}
        onError={(error) => setToast({
          severity: 'error',
          message: resolveUserFacingError(error, t('common.feedback.save.failed')),
        })}
        onSave={savePlan}
        onRefresh={planMaster.reload}
        open={planDialogOpen}
        plan={editingPlan}
        selectedYear={planFiscalYear}
        t={t}
      />

      <ActionPlanDetailDialog
        onClose={() => setDetailPlan(null)}
        open={Boolean(detailPlan)}
        plan={detailPlan}
        selectedYear={planFiscalYear}
        t={t}
      />

      <ActionPlanResultDialog
        canMutate
        fiscalYear={planFiscalYear}
        onClose={() => setResultPlan(null)}
        onDeleteEvidence={planMaster.deleteEvidence}
        onError={(error) => setToast({
          severity: 'error',
          message: resolveUserFacingError(error, t('common.feedback.save.failed')),
        })}
        onLoadResultInputOptions={planMaster.loadActionPlanResultInputOptions}
        onRefresh={planMaster.reload}
        onSaveResult={savePlanResult}
        onUploadEvidence={planMaster.uploadEvidence}
        open={Boolean(resultPlan)}
        plan={resultPlan}
        t={t}
      />

      <TargetDialog
        baselineYears={targetMaster.baselineYears}
        cycleId={selectedTargetCycleId}
        inventories={targetMaster.inventories}
        mode={targetDialogMode}
        onClose={() => setTargetDialogOpen(false)}
        onSave={async (targetBatch) => {
          try {
            await targetMaster.saveTargetPeriod(targetBatch, selectedTargetCycleId);
            setToast({ severity: 'success', message: t('climateAction.feedback.targetSaved') });
          } catch (saveError) {
            setToast({
              severity: 'error',
              message: resolveUserFacingError(saveError, t('common.feedback.save.failed')),
            });
            throw saveError;
          }
        }}
        open={targetDialogOpen}
        readOnly={targetDialogMode === 'view'}
        targetCycles={targetMaster.targetCycles}
        targets={targetMaster.targets}
        t={t}
      />

      <TargetPeriodConfirmDialog
        action={confirmTargetAction?.action}
        onClose={() => setConfirmTargetAction(null)}
        onConfirm={confirmTargetPeriodAction}
        open={Boolean(confirmTargetAction)}
        period={confirmTargetAction?.cycle}
        t={t}
      />

      <AppToast toast={toast} onClose={() => setToast(null)} />
    </Box>
  );
}
